/**
 * Tab Stimulus Controller
 *
 * This Stimulus controller provides simple tab switching functionality.
 * It handles tab button clicks and maintains active state.
 */

import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

class Tab extends Controller {
  static targets = ['tabButton', 'tabPanel'];
  static values = {
    activeTab: String,
  };

  declare readonly tabButtonTargets: HTMLButtonElement[];
  declare readonly tabPanelTargets: HTMLElement[];
  declare readonly hasTabButtonTargets: boolean;
  declare readonly hasTabPanelTargets: boolean;
  declare activeTabValue: string;

  /**
   * Called when the controller is connected to the DOM
   */
  connect() {
    this.updateTabDisplay();
    this.setupKeyboardNavigation();
  }

  /**
   * Switch to a specific tab using action parameters
   */
  switchTab(event: Event & { params: { tabId: string } }) {
    const { tabId } = event.params;

    if (!tabId) {
      return;
    }

    // Check if the button is disabled
    const button = event.currentTarget as HTMLButtonElement;
    if (button.disabled) {
      return;
    }

    this.activeTabValue = tabId;
  }

  /**
   * Handle changes to the active tab value
   */
  activeTabValueChanged() {
    this.updateTabDisplay();
    this.dispatchTabChange();
  }

  /**
   * Update the visual state of tabs and panels
   */
  private updateTabDisplay() {
    // Update tab buttons
    this.tabButtonTargets.forEach((button) => {
      const tabId = this.getTabIdFromElement(button);
      const isActive = tabId === this.activeTabValue;

      // Update ARIA attributes
      button.setAttribute('aria-selected', isActive.toString());
      button.setAttribute('tabindex', isActive ? '0' : '-1');

      // Update CSS classes
      const activeClasses = button.dataset['ui-TabActiveClasses'];
      const inactiveClasses = button.dataset['ui-TabInactiveClasses'];

      if (activeClasses && inactiveClasses) {
        if (isActive) {
          button.className = activeClasses;
        } else {
          button.className = inactiveClasses;
        }
      }
    });

    // Update tab panels
    this.tabPanelTargets.forEach((panel) => {
      const tabId = this.getTabIdFromElement(panel);
      const isActive = tabId === this.activeTabValue;

      if (isActive) {
        panel.classList.remove('hidden');
        panel.classList.add('block');
      } else {
        panel.classList.remove('block');
        panel.classList.add('hidden');
      }
    });
  }

  /**
   * Get tab ID from element's data attribute
   */
  private getTabIdFromElement(element: HTMLElement): string | undefined {
    // Try different possible attribute names to be robust
    return (
      element.dataset.uiTabTabIdParam ||
      element.dataset.tabId ||
      element.getAttribute('data-ui--tab-tab-id-param') ||
      undefined
    );
  }

  /**
   * Dispatch a custom event when tab changes
   */
  private dispatchTabChange() {
    this.dispatch('changed', {
      detail: {
        activeTab: this.activeTabValue,
        tabElement: this.element,
        activeButton: this.tabButtonTargets.find(
          (tab) => this.getTabIdFromElement(tab) === this.activeTabValue
        ),
        activePanel: this.tabPanelTargets.find(
          (panel) => this.getTabIdFromElement(panel) === this.activeTabValue
        ),
      },
    });
  }

  /**
   * Setup keyboard navigation for tab switching
   */
  private setupKeyboardNavigation() {
    this.tabButtonTargets.forEach((button) => {
      button.addEventListener('keydown', (event) => {
        this.handleKeydown(event);
      });
    });
  }

  /**
   * Handle keyboard navigation
   */
  private handleKeydown(event: KeyboardEvent) {
    const currentButton = event.currentTarget as HTMLButtonElement;
    const currentIndex = this.tabButtonTargets.indexOf(currentButton);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : this.tabButtonTargets.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = currentIndex < this.tabButtonTargets.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = this.tabButtonTargets.length - 1;
        break;
      default:
        return;
    }

    // Find the next enabled tab
    while (nextIndex !== currentIndex) {
      const nextButton = this.tabButtonTargets[nextIndex];
      if (!nextButton.disabled) {
        const tabId = this.getTabIdFromElement(nextButton);
        if (tabId) {
          this.activeTabValue = tabId;
          nextButton.focus();
        }
        break;
      }
      nextIndex = nextIndex < this.tabButtonTargets.length - 1 ? nextIndex + 1 : 0;
    }
  }
}

application.register('ui--tab', Tab);
